import { useQuery } from "react-query";
import { useContext } from "react";
import { AuthContext } from "../../Context/AuthContext";

const useSearchVessels = (searchTerm, filters = {}) => {
  const { token } = useContext(AuthContext);

  const searchVessels = async () => {
    if (!token) {
      throw new Error("Token is missing");
    }

    // Build search body based on search term and filters
    const searchBody = {};

    // Add search term if provided
    if (searchTerm && searchTerm.trim()) {
      const searchTermLower = searchTerm.trim().toLowerCase();
      
      // Map common search terms to vessel types
      const vesselTypeMap = {
        'ahts': 'AHTS 001',
        'psv': 'PSV 001',
        'sev': 'SEV 001',
        'fsiv': 'FSIV 001',
        'platform supply': 'PSV 001',
        'supply vessel': 'PSV 001',
        'anchor handling': 'AHTS 001',
        'tug supply': 'AHTS 001',
        'special purpose': 'SEV 001',
        'specialized': 'SEV 001',
        'fast supply': 'FSIV 001',
        'intervention': 'FSIV 001'
      };
      
      // Check if search term matches any vessel type
      const matchedType = Object.keys(vesselTypeMap).find(key => 
        searchTermLower.includes(key)
      );
      
      if (matchedType) {
        searchBody.vesselType = vesselTypeMap[matchedType];
      } else {
        // If no match found, try to use the search term directly
        // but only if it matches one of the valid vessel types
        const validTypes = ['AHTS 001', 'PSV 001', 'SEV 001', 'FSIV 001'];
        if (validTypes.includes(searchTerm.trim())) {
          searchBody.vesselType = searchTerm.trim();
        }
      }
    }

    // Map filters to API fields
    if (filters.type && filters.type.length > 0) {
      searchBody.vesselType = filters.type[0]; // API expects single vesselType
    }

    if (filters.use && filters.use.length > 0) {
      searchBody.vesselUse = filters.use; // API expects array of vesselUse
    }

    if (filters.classification && filters.classification.length > 0) {
      searchBody.classification = filters.classification[0]; // API expects single classification
    }

    if (filters.paxAccommodation && filters.paxAccommodation.length > 0) {
      // Convert pax accommodation ranges to number
      const paxRange = filters.paxAccommodation[0];
      if (paxRange.includes('-')) {
        const [min] = paxRange.split('-').map(num => parseInt(num));
        searchBody.numberOfPassengers = min;
      } else if (paxRange.includes('+')) {
        const min = parseInt(paxRange.replace('+', ''));
        searchBody.numberOfPassengers = min;
      }
    }

    if (filters.price && filters.price.length > 0) {
      // Convert price ranges to pricing
      const priceRange = filters.price[0];
      if (priceRange.includes('-')) {
        const [min] = priceRange.replace(/[$,]/g, "").split('-').map(num => parseInt(num));
        searchBody.pricing = min;
      } else if (priceRange.includes('+')) {
        const min = parseInt(priceRange.replace(/[$,+]/g, ""));
        searchBody.pricing = min;
      }
    }

    if (filters.paymentPlan && filters.paymentPlan.length > 0) {
      // Convert payment plan to number
      const plan = filters.paymentPlan[0];
      const planMap = {
        'Daily': 1,
        'Weekly': 2,
        'Monthly': 3
      };
      searchBody.paymentPlan = planMap[plan] || 1;
    }

    // Set default serviceType to "lease" if not specified
    if (!searchBody.serviceType) {
      searchBody.serviceType = "lease";
    }

    const endpoint = `${process.env.REACT_APP_SERVER_URL}/charter/search`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(searchBody),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to search vessels (${response.status})`);
      }

      return responseData;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error("Network error: Unable to connect to server");
      }
      throw error;
    }
  };

  const {
    data: searchResults,
    error: searchError,
    isLoading: searchLoading,
    refetch: refetchSearch,
  } = useQuery(
    ["searchVessels", searchTerm, filters],
    searchVessels,
    {
      enabled: !!token && (!!searchTerm || Object.values(filters).some(filters => filters.length > 0)),
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  return {
    searchResults: searchResults?.data || [],
    searchError,
    searchLoading,
    refetchSearch,
  };
};

export default useSearchVessels; 